import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { ChevronRight, MoreVertical, Edit, Archive, ArchiveRestore, Eye } from 'lucide-react';
import { STATUS_PILL_CLASS, TENANT_TYPE_CONFIG, statusLabel } from './helpers';
import { usePermissions } from '@/hooks/usePermissions';
import { setImpersonation } from '@/lib/impersonation';

/**
 * Phase 1D.2 → 1D.3 — Tenant list presentation.
 *
 * Renders a desktop table at md+ and stacked cards on mobile. Row / card
 * is the drill-down target into the detail page. A per-row kebab menu
 * (1D.3) exposes Edit, Archive and Restore actions; all bubble up via callbacks
 * to the parent for state ownership and refresh-on-success.
 *
 * Root tenants don't get a kebab (they're system-managed). Archived
 * tenants get only the Edit option (Archive on an already-archived row
 * is meaningless).
 *
 * Props
 *   tenants          — Tenant[] (already filtered)
 *   parentNameById   — Map<tenant_id, name>
 *   onEdit           — (tenant) => void
 *   onArchive        — (tenant) => void
 *   onRestore        — (tenant) => void
 */
export default function TenantList({ tenants, parentNameById, onEdit, onArchive, onRestore }) {
  const navigate = useNavigate();
  const goTo = (tenant) => navigate(`/admin/tenants/${tenant.tenant_id}`);

  return (
    <div className="bg-boss-surface border border-boss-border rounded-xl overflow-hidden">
      {/* Desktop table — md+ */}
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full text-sm" data-testid="tenants-table">
          <thead>
            <tr className="border-b border-boss-border">
              {['Name', 'Type', 'Parent', 'Status', 'Created'].map((h) => (
                <th
                  key={h}
                  className="text-left py-3 px-4 text-xs font-medium text-boss-text-muted uppercase tracking-wider"
                >
                  {h}
                </th>
              ))}
              <th className="w-12" />
            </tr>
          </thead>
          <tbody>
            {tenants.map((t) => {
              const typeCfg =
                TENANT_TYPE_CONFIG[t.tenant_type] || TENANT_TYPE_CONFIG.account;
              const TypeIcon = typeCfg.icon;
              const parentName = t.parent_tenant_id
                ? parentNameById.get(t.parent_tenant_id) || '—'
                : '—';
              return (
                <tr
                  key={t.tenant_id}
                  onClick={() => goTo(t)}
                  className="border-b border-boss-border last:border-b-0 hover:bg-boss-accent-muted cursor-pointer transition-colors"
                  data-testid={`tenant-row-${t.tenant_id}`}
                >
                  <td className="py-3 px-4">
                    <div className="font-medium text-boss-text">{t.name}</div>
                    <div className="text-[11px] text-boss-text-muted font-mono mt-0.5">
                      {t.slug}
                    </div>
                  </td>
                  <td className="py-3 px-4">
                    <span className="inline-flex items-center gap-1.5 text-boss-text-secondary">
                      <TypeIcon size={14} />
                      {typeCfg.label}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-boss-text-secondary">
                    {parentName}
                  </td>
                  <td className="py-3 px-4">
                    <span
                      className={`inline-flex text-xs px-2 py-0.5 rounded-full w-fit ${STATUS_PILL_CLASS[t.status] || ''}`}
                    >
                      {statusLabel(t.status)}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-boss-text-secondary text-xs">
                    {formatDate(t.created_at)}
                  </td>
                  <td className="py-3 px-2 text-right">
                    <RowKebab
                      tenant={t}
                      onEdit={onEdit}
                      onArchive={onArchive}
                      onRestore={onRestore}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Mobile cards — below md.
        * Note: changed from <button> in 1D.2 to <div role="button"> here
        * because we now host a nested <button> (the kebab) inside the
        * tappable card surface, and button-in-button is invalid HTML. */}
      <div className="md:hidden p-3 space-y-2.5">
        {tenants.map((t) => {
          const typeCfg =
            TENANT_TYPE_CONFIG[t.tenant_type] || TENANT_TYPE_CONFIG.account;
          const TypeIcon = typeCfg.icon;
          const parentName = t.parent_tenant_id
            ? parentNameById.get(t.parent_tenant_id) || null
            : null;
          return (
            <div
              key={t.tenant_id}
              onClick={() => goTo(t)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  goTo(t);
                }
              }}
              role="button"
              tabIndex={0}
              className="w-full text-left bg-boss-base border border-boss-border rounded-lg p-3.5 hover:border-boss-border-hover cursor-pointer transition focus:outline-none focus:ring-2 focus:ring-violet-500/40"
              data-testid={`tenant-card-${t.tenant_id}`}
            >
              <div className="flex items-start justify-between gap-2 mb-2">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-boss-text truncate">
                    {t.name}
                  </p>
                  <p className="text-[11px] text-boss-text-muted flex items-center gap-1.5 mt-0.5">
                    <TypeIcon size={11} />
                    {typeCfg.label}
                    <span className="opacity-50">·</span>
                    <span className="font-mono truncate">{t.slug}</span>
                  </p>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <span
                    className={`inline-flex text-[10px] px-2 py-0.5 rounded-full ${STATUS_PILL_CLASS[t.status] || ''}`}
                  >
                    {statusLabel(t.status)}
                  </span>
                  <RowKebab
                    tenant={t}
                    onEdit={onEdit}
                    onArchive={onArchive}
                    onRestore={onRestore}
                  />
                </div>
              </div>
              <div className="flex items-center justify-between pt-2 mt-1 border-t border-boss-border text-[11px] text-boss-text-muted">
                <span className="truncate">
                  {parentName ? `Parent: ${parentName}` : 'Direct customer'}
                  <span className="mx-1.5 opacity-60">·</span>
                  {formatDate(t.created_at)}
                </span>
                <ChevronRight
                  size={14}
                  className="shrink-0 ml-2 text-boss-text-muted"
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Row kebab menu ────────────────────────────────────────────────

/**
 * Per-row Edit / Archive / Act-as dropdown. Hidden entirely for root
 * tenants (system-managed). Archive option is hidden on already-archived
 * rows.
 *
 * Click is stopped from propagating to the row so opening the menu
 * doesn't accidentally drill into the detail page.
 *
 * Phase 2E.2 — "Act as this tenant" is resolved here rather than
 * threaded down as an `onImpersonate` prop, because TenantList has two
 * call sites (the Tenants page and the Sub-tenants tab of TenantDetail)
 * and both should offer the action identically. Handling it in the
 * kebab means neither parent's prop chain changes.
 *
 * The entry is HIDDEN, not disabled, under any of three conditions:
 *
 *   1. The caller lacks `tenants.impersonate`. This is gate 2 in
 *      middleware/tenant_scoping.py `_apply_tenant_override`.
 *   2. The tenant is root — already covered by the early return below.
 *   3. The tenant's status is not "active". The backend's
 *      `_IMPERSONABLE_STATUSES` is exactly ("active",), so offering the
 *      action on a suspended or archived tenant guarantees a 403.
 *
 * Hidden rather than greyed out because a disabled "Act as this tenant"
 * invites "why not?", and answering that properly is 2F's job (dormant
 * key rendering), not this slice's.
 */
function RowKebab({ tenant, onEdit, onArchive, onRestore }) {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState(null);
  const buttonRef = useRef(null);
  const menuRef = useRef(null);
  const { hasPermission } = usePermissions();

  // Close on outside click, Escape, or any scroll/resize.
  //
  // The menu is portalled to document.body, so it is NOT a DOM
  // descendant of this component — an outside-click check against a
  // single wrapper ref would treat clicks on the menu itself as
  // "outside" and dismiss before the item's onClick ran. Both refs are
  // checked.
  //
  // Scroll closes rather than repositions: the row the menu is anchored
  // to can leave the viewport entirely, and a fixed-position menu
  // floating over unrelated rows is worse than one that dismissed.
  // `true` for capture so scrolls inside the table wrapper are caught,
  // not just window scroll.
  useEffect(() => {
    if (!open) return undefined;
    const handleClick = (e) => {
      if (buttonRef.current && buttonRef.current.contains(e.target)) return;
      if (menuRef.current && menuRef.current.contains(e.target)) return;
      setOpen(false);
    };
    const handleEsc = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    const handleReflow = () => setOpen(false);
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleEsc);
    window.addEventListener('scroll', handleReflow, true);
    window.addEventListener('resize', handleReflow);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleEsc);
      window.removeEventListener('scroll', handleReflow, true);
      window.removeEventListener('resize', handleReflow);
    };
  }, [open]);

  // Position after the menu exists so its real height is measurable.
  //
  // Flip-up when there isn't room below: the last row of a table is the
  // common case, and it is exactly where a down-opening menu runs off
  // the fold. useLayoutEffect (not useEffect) so the correction happens
  // before paint — the menu renders at opacity-0 until `coords` is set,
  // so there is no flash at the wrong position.
  useLayoutEffect(() => {
    if (!open || !buttonRef.current || !menuRef.current) return;
    const anchor = buttonRef.current.getBoundingClientRect();
    const menu = menuRef.current.getBoundingClientRect();
    const GAP = 4;
    const MARGIN = 8;

    const roomBelow = window.innerHeight - anchor.bottom;
    const openUp = roomBelow < menu.height + GAP + MARGIN && anchor.top > menu.height;

    let top = openUp ? anchor.top - menu.height - GAP : anchor.bottom + GAP;
    top = Math.max(MARGIN, Math.min(top, window.innerHeight - menu.height - MARGIN));

    // Right-aligned to the kebab, then clamped so a menu near the left
    // edge (narrow viewport, collapsed sidebar) can't go off-screen.
    let left = anchor.right - menu.width;
    left = Math.max(MARGIN, Math.min(left, window.innerWidth - menu.width - MARGIN));

    setCoords({ top, left });
  }, [open]);

  if (tenant.tenant_type === 'root') return null;

  const canArchive = tenant.status !== 'archived';
  // 2H — the way back. `update_tenant` has always accepted
  // `status: "active"`, so archiving was reversible from the moment it
  // shipped; the only thing missing was somewhere to click. It was
  // reachable through Edit, but Edit does not offer `archived` as a status
  // option, so nobody would think to look there for a restore.
  //
  // Offered for any non-active tenant, not just archived ones: a suspended
  // tenant needs the same one-click way back, and `update_tenant` treats
  // both the same.
  const canRestore = Boolean(onRestore) && tenant.status !== 'active';
  const canImpersonate =
    tenant.status === 'active' && hasPermission('tenants.impersonate');

  // Store the target, then hard-reload. Every mounted page is holding
  // data fetched as the previous tenant, and a reload is the only way to
  // guarantee all of it is refetched under the new header — the same
  // reasoning as the Exit control in ImpersonationBanner. Navigating to
  // the dashboard rather than staying on the tenant admin page because
  // the admin surfaces are root-only: staying here would land the user
  // on a screen of 403s.
  const handleImpersonate = () => {
    if (setImpersonation(tenant.tenant_id, tenant.name)) {
      window.location.assign('/');
    }
  };

  const itemClass =
    'w-full text-left flex items-center gap-2 px-3 py-2 text-sm whitespace-nowrap transition';

  const menu = (
    <div
      ref={menuRef}
      role="menu"
      onClick={(e) => e.stopPropagation()}
      style={{
        position: 'fixed',
        top: coords ? coords.top : 0,
        left: coords ? coords.left : 0,
        opacity: coords ? 1 : 0,
      }}
      className="z-[9999] min-w-[180px] bg-boss-surface border border-boss-border rounded-lg shadow-lg py-1"
      data-testid={`tenant-menu-${tenant.tenant_id}`}
    >
      <button
        type="button"
        role="menuitem"
        onClick={(e) => {
          e.stopPropagation();
          setOpen(false);
          onEdit(tenant);
        }}
        className={`${itemClass} text-boss-text hover:bg-boss-elevated`}
        data-testid={`tenant-edit-${tenant.tenant_id}`}
      >
        <Edit size={14} />
        Edit
      </button>
      {canImpersonate && (
        <button
          type="button"
          role="menuitem"
          onClick={(e) => {
            e.stopPropagation();
            setOpen(false);
            handleImpersonate();
          }}
          className={`${itemClass} text-boss-text hover:bg-boss-elevated`}
          data-testid={`tenant-impersonate-${tenant.tenant_id}`}
        >
          <Eye size={14} />
          Act as this tenant
        </button>
      )}
      {canRestore && (
        <button
          type="button"
          role="menuitem"
          onClick={(e) => {
            e.stopPropagation();
            setOpen(false);
            onRestore(tenant);
          }}
          className={`${itemClass} text-emerald-700 dark:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-600/10`}
          data-testid={`tenant-restore-${tenant.tenant_id}`}
        >
          <ArchiveRestore size={14} />
          Restore
        </button>
      )}
      {canArchive && (
        <button
          type="button"
          role="menuitem"
          onClick={(e) => {
            e.stopPropagation();
            setOpen(false);
            onArchive(tenant);
          }}
          className={`${itemClass} text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-600/10`}
          data-testid={`tenant-archive-${tenant.tenant_id}`}
        >
          <Archive size={14} />
          Archive
        </button>
      )}
    </div>
  );

  return (
    <div className="relative inline-block" onClick={(e) => e.stopPropagation()}>
      <button
        ref={buttonRef}
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setOpen((o) => {
            // Drop stale coords on close so the next open measures fresh
            // rather than flashing at the previous row's position.
            if (o) setCoords(null);
            return !o;
          });
        }}
        aria-label="Actions"
        aria-haspopup="menu"
        aria-expanded={open}
        className="p-1.5 rounded hover:bg-boss-elevated text-boss-text-muted hover:text-boss-text transition"
        data-testid={`tenant-kebab-${tenant.tenant_id}`}
      >
        <MoreVertical size={16} />
      </button>
      {open && createPortal(menu, document.body)}
    </div>
  );
}


// ─── Helpers ───────────────────────────────────────────────────────

const dateFmt = new Intl.DateTimeFormat('en-GB', {
  day: '2-digit',
  month: 'short',
  year: 'numeric',
});

function formatDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return dateFmt.format(d);
}
